import Close from '@mui/icons-material/Close';
import Alert, { AlertColor } from '@mui/material/Alert';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Portal from '@mui/material/Portal';
import Slide, { SlideProps } from '@mui/material/Slide';
import Snackbar, { SnackbarProps } from '@mui/material/Snackbar';
import { observer } from 'mobx-react-lite';
import { createPortal } from 'react-dom';

import { useStores } from '~store';
import { Alert as AlertType } from '~types/AlertTypes';

const Toasters = observer(() => {
  const { toasterStore } = useStores();

  const SlideTransition = (props: SlideProps) => {
    return (
      <Slide
        {...props}
        direction="up"
        mountOnEnter
        unmountOnExit
        onExited={toasterStore.process}
      />
    );
  };

  return toasterStore.current.cata({
    Just: (alert: AlertType) => (
      <Portal>
        <SnackbarAlert
          alert={alert}
          onClose={() => {
            toasterStore.hide();
            toasterStore.process();
          }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            toasterStore.hide();
          }}
          onAlertClick={() => {
            if (alert.onClick) {
              alert.onClick();
              toasterStore.hide();
            }
          }}
          TransitionComponent={SlideTransition}
        />
      </Portal>
    ),
    Nothing: () => <></>,
  });
});

const PermanentToaster = observer(() => {
  const { toasterStore } = useStores();

  const SlideDownTransition = (props: SlideProps) => {
    return <Slide {...props} direction="down" mountOnEnter unmountOnExit />;
  };

  const SlideUpTransition = (props: SlideProps) => {
    return <Slide {...props} direction="up" mountOnEnter unmountOnExit />;
  };

  return toasterStore.permanent?.cata({
    Just: (alert: AlertType) => {
      const isSlideUpTransition = alert.anchorOrigin?.vertical === 'bottom';

      return (
        <Portal>
          <SnackbarAlert
            key={alert.id}
            alert={alert}
            onAutoHide={() => {
              toasterStore.hidePermanentAlert();
              alert.onAutoHide?.();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              toasterStore.hidePermanentAlert();
            }}
            onAlertClick={alert.onClick}
            TransitionComponent={
              isSlideUpTransition ? SlideUpTransition : SlideDownTransition
            }
          />
        </Portal>
      );
    },
    Nothing: () => <></>,
  });
});

function SnackbarAlert({
  alert,
  onAutoHide,
  onClose,
  onClick,
  onAlertClick,
  TransitionComponent,
  isPermanent,
}: {
  alert: AlertType;
  onAutoHide?: () => void;
  onClose?: () => void;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onAlertClick?: () => void;
  TransitionComponent?: SnackbarProps['TransitionComponent'];
  isPermanent?: boolean;
  hideCloseButton?: boolean;
}) {
  if (alert.isPermanent) {
    return createPortal(
      <Snackbar
        anchorOrigin={alert.anchorOrigin ?? { vertical: 'top', horizontal: 'center' }}
        open
        autoHideDuration={alert.autoHideDuration}
        onClose={(_event, reason) => {
          if (reason === 'timeout') {
            onAutoHide?.();
          }
        }}
        slots={{ transition: TransitionComponent }}
        sx={alert.sx}
      >
        <Box
          data-test-id={'toaster-test-id'}
          sx={{ display: 'flex', cursor: alert.onClick ? 'pointer' : undefined }}
        >
          <Alert
            severity={alert.kind as AlertColor}
            onClick={onAlertClick}
            sx={{ width: '100%' }}
            action={
              alert.hideCloseButton ? undefined : (
                <IconButton color="inherit" size="small" onClick={onClick}>
                  <Close sx={{ width: 14, height: 14 }}></Close>
                </IconButton>
              )
            }
          >
            {alert?.message}
          </Alert>
        </Box>
      </Snackbar>,
      document.getElementById('permanent-toast')!,
    );
  }

  return (
    <Snackbar
      anchorOrigin={
        (alert.anchorOrigin ?? isPermanent)
          ? { vertical: 'top', horizontal: 'center' }
          : { vertical: 'bottom', horizontal: 'center' }
      }
      open={alert.display}
      onClose={onClose}
      autoHideDuration={isPermanent ? null : alert.autoHideDuration}
      slots={{ transition: TransitionComponent }}
      sx={alert.sx}
    >
      <Box
        data-test-id={'toaster-test-id'}
        sx={{ display: 'flex', cursor: alert.onClick ? 'pointer' : undefined }}
      >
        <Alert
          severity={alert.kind as AlertColor}
          sx={{ width: '100%' }}
          action={
            alert.hideCloseButton ? undefined : (
              <IconButton color="inherit" size="small" onClick={onClick}>
                <Close sx={{ width: 14, height: 14 }}></Close>
              </IconButton>
            )
          }
          onClick={onAlertClick}
        >
          {alert?.message}
        </Alert>
      </Box>
    </Snackbar>
  );
}

export { PermanentToaster, Toasters };
